import type { ApprovalChainPortalDto, CollectionItemStatus } from "../api/types";
import { getItemStatusDisplay } from "../lib/statusMap";
import { StatusBadge, type StatusTone } from "@/design-system/components/ui";

// Thin portal wrapper over the shared design-system <StatusBadge>. Keeps the
// collection-item status vocabulary (labels + plain-language "what's next"
// hints from statusMap) while the visual treatment now comes from the shared
// kit, so every portal renders the same pill.
const TONE_MAP: Record<string, StatusTone> = {
  neutral: "neutral",
  progress: "progress",
  warn: "warn",
  ok: "ok",
  muted: "muted",
};

export function StatusPill({
  status,
  approvalChain,
  "data-testid": testId,
}: {
  status: CollectionItemStatus;
  /** Approval chains (ruling R-C) — when present the pill states the
   *  caller's chain status (the server's label), not the item status. */
  approvalChain?: Pick<
    ApprovalChainPortalDto,
    "myStatus" | "statusLabel" | "isCompleted"
  > | null;
  "data-testid"?: string;
}) {
  if (approvalChain) {
    return (
      <StatusBadge
        tone={chainTone(approvalChain)}
        hint={chainHint(approvalChain)}
        data-status={status}
        data-chain-status={approvalChain.myStatus}
        data-testid={testId}
      >
        {approvalChain.statusLabel}
      </StatusBadge>
    );
  }
  const display = getItemStatusDisplay(status);
  return (
    <StatusBadge
      tone={TONE_MAP[display.tone]}
      hint={display.description}
      struck={display.tone === "muted"}
      data-status={status}
      data-testid={testId}
    >
      {display.label}
    </StatusBadge>
  );
}

function chainTone(chain: Pick<ApprovalChainPortalDto, "myStatus" | "isCompleted">): StatusTone {
  if (chain.isCompleted) return "ok";
  return chain.myStatus === "YourTurn" ? "progress" : "neutral";
}

function chainHint(chain: Pick<ApprovalChainPortalDto, "myStatus" | "isCompleted">): string {
  if (chain.isCompleted) return "Every party has signed this document.";
  switch (chain.myStatus) {
    case "YourTurn":
      return "Open this item to review the document and complete your step.";
    case "Done":
      return "You've completed your step on this document.";
    case "Waiting":
      return "Nothing for you to do right now.";
  }
}
